import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { FiCalendar, FiMapPin, FiClock, FiMessageSquare, FiEye } from 'react-icons/fi';
import { PropertyThumbnail } from '../components/OptimizedImage';
import './Dashboard.css';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, upcoming, past

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await api.get('/api/bookings?type=guest');
      setBookings(response.data.bookings);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error(t('dashboard.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-warning',
      confirmed: 'badge-success',
      completed: 'badge-info',
      cancelled: 'badge-danger'
    };
    return badges[status] || 'badge-info';
  };

  const filterBookings = (bookings) => {
    const now = new Date();

    switch (filter) {
      case 'upcoming':
        return bookings.filter(b => new Date(b.check_in) >= now && b.status !== 'cancelled');
      case 'past':
        return bookings.filter(b => new Date(b.check_out) < now || b.status === 'completed');
      default:
        return bookings;
    }
  };

  const filteredBookings = filterBookings(bookings);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="container">
        <div className="dashboard-header">
          <div>
            <h1>{t('dashboard.welcomeBack')}, {user?.first_name}!</h1>
            <p className="dashboard-subtitle">{t('dashboard.manageBookings')}</p>
          </div>
          <Link to="/properties" className="btn btn-primary">
            {t('dashboard.browseProperties')}
          </Link>
        </div>

        {/* Filter Tabs */}
        <div className="filter-tabs">
          <button
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            {t('dashboard.allBookings')} ({bookings.length})
          </button>
          <button
            className={`filter-tab ${filter === 'upcoming' ? 'active' : ''}`}
            onClick={() => setFilter('upcoming')}
          >
            {t('dashboard.upcoming')}
          </button>
          <button
            className={`filter-tab ${filter === 'past' ? 'active' : ''}`}
            onClick={() => setFilter('past')}
          >
            {t('dashboard.past')}
          </button>
        </div>

        {/* Bookings List */}
        {filteredBookings.length === 0 ? (
          <div className="no-bookings">
            <h3>{t('dashboard.noBookings')}</h3>
            <p>{t('dashboard.startExploring')}</p>
            <Link to="/properties" className="btn btn-primary mt-3">
              {t('dashboard.exploreProperties')}
            </Link>
          </div>
        ) : (
          <div className="bookings-grid">
            {filteredBookings.map((booking) => (
              <div
                key={booking.id}
                className="booking-card"
              >
                <div className="booking-image">
                  {booking.property.images && booking.property.images.length > 0 ? (
                    <PropertyThumbnail
                      src={booking.property.images.find(img => img.is_primary)?.image_url ||
                           booking.property.images[0]?.image_url}
                      width={400}
                      height={300}
                      alt={booking.property.title}
                    />
                  ) : (
                    <div className="no-image">{t('dashboard.noImage')}</div>
                  )}
                  <span className={`status-badge badge ${getStatusBadge(booking.status)}`}>
                    {t(`booking.${booking.status}`)}
                  </span>
                </div>

                <div className="booking-info">
                  <h3 className="booking-title">{booking.property.title}</h3>

                  <div className="booking-location">
                    <FiMapPin size={14} />
                    <span>{booking.property.city}, {booking.property.country}</span>
                  </div>

                  <div className="booking-dates">
                    <div className="date-item">
                      <FiCalendar size={14} />
                      <span>Check-in: {new Date(booking.check_in).toLocaleDateString()}</span>
                    </div>
                    <div className="date-item">
                      <FiClock size={14} />
                      <span>{booking.nights} {booking.nights === 1 ? t('dashboard.night') : t('dashboard.nights')}</span>
                    </div>
                  </div>

                  <div className="booking-footer">
                    <div className="booking-guests">
                      {booking.num_guests} {booking.num_guests === 1 ? t('dashboard.guest') : t('dashboard.guests')}
                    </div>
                    <div className="booking-price">
                      R$ {booking.total_price}
                    </div>
                  </div>

                  <div className="booking-actions">
                    <button
                      onClick={() => navigate(`/bookings/${booking.id}`)}
                      className="btn btn-sm btn-secondary"
                    >
                      <FiEye size={16} /> {t('dashboard.viewDetails')}
                    </button>
                    <button
                      onClick={() => navigate(`/messages?userId=${booking.property.host.id}`)}
                      className="btn btn-sm btn-primary"
                    >
                      <FiMessageSquare size={16} /> {t('dashboard.messageHost')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
